import React, { useState, useEffect } from "react";
import api from "../../../services/httpService";

const CancellationPolicyAdmin = () => {
  const [policy, setPolicy] = useState({
    title: "",
    description: "",
    ImageKey: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [policyId, setPolicyId] = useState(null);

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/sentinix/cancelationpolicy");
      if (res.data.success && res.data.data) {
        const data = Array.isArray(res.data.data)
          ? res.data.data.sort((a, b) => b.id - a.id)[0]
          : res.data.data;

        if (data) {
          setPolicy({
            title:       data.title       || "",
            description: data.description || "",
            ImageKey:    data.ImageKey    || "",
          });
          setPolicyId(data.id);
        }
      }
    } catch (err) {
      console.error("Failed to load cancellation policy:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage("");
    try {
      const formData = new FormData();
      formData.append("title",       policy.title       || "");
      formData.append("description", policy.description || "");

      if (selectedImage) {
        formData.append("ImageKey", selectedImage);
      } else if (policy.ImageKey) {
        formData.append("ImageKey", policy.ImageKey);
      }

      let res;
      if (policyId) {
        res = await api.put(`/sentinix/cancelationpolicy/${policyId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post("/sentinix/cancelationpolicy", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.data.success) {
        setMessage("Cancellation policy saved successfully!");
        setSelectedImage(null);
        setImagePreview(null);
        setIsEditing(false);
        fetchPolicy();
      } else {
        setMessage(res.data.error || "Save failed");
      }
    } catch (err) {
      console.error("Save error:", err);
      setMessage(err.response?.data?.message || "Save failed");
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage(""), 3000);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <div style={{ textAlign: "center" }}>
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: "900px" }}>
      <h1 style={{ fontSize: "1.8rem", fontWeight: 700, color: "#1a1a2e", marginBottom: "1.5rem" }}>
        Cancellation Policy
      </h1>

      {message && (
        <div style={{
          padding: "0.75rem 1.25rem",
          borderRadius: "8px",
          marginBottom: "1rem",
          fontWeight: 500,
          background: message.includes("success") ? "#e8f5e9" : "#ffebee",
          color: message.includes("success") ? "#2e7d32" : "#c62828",
          border: `1px solid ${message.includes("success") ? "#a5d6a7" : "#ef9a9a"}`,
        }}>
          {message}
        </div>
      )}

      <div style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "1.5rem 2rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", borderBottom: "1px solid #f0f0f0", paddingBottom: "1rem" }}>
          <div>
            <h2 style={{ fontSize: "1.1rem", fontWeight: 600, color: "#1a1a2e", margin: 0 }}>
              Policy Content
            </h2>
            <p style={{ fontSize: "0.8rem", color: "#888", margin: "4px 0 0" }}>
              Manage cancellation policy page
            </p>
          </div>
          <button
            onClick={() => setIsEditing(!isEditing)}
            style={{
              background: isEditing ? "#6b7280" : "#2196f3",
              color: "#fff",
              border: "none",
              padding: "0.4rem 1rem",
              borderRadius: "6px",
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            {isEditing ? "Cancel" : "Edit"}
          </button>
        </div>

        {isEditing ? (
          <>
            {/* Title */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", fontSize: "0.9rem", fontWeight: 500, color: "#333", marginBottom: "0.5rem" }}>
                Page Title <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={policy.title}
                onChange={(e) => setPolicy({ ...policy, title: e.target.value })}
                placeholder="e.g. Cancellation Policy"
                style={{
                  width: "100%", padding: "0.6rem 1rem", border: "1px solid #ddd",
                  borderRadius: "8px", fontSize: "0.95rem", color: "#333",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>

            {/* Banner Image */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", fontSize: "0.9rem", fontWeight: 500, color: "#333", marginBottom: "0.5rem" }}>
                Banner Image
              </label>
              <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: "0.5rem" }}>
                This image shows on the Cancellation Policy page hero section
              </p>

              {(imagePreview || policy.ImageKey) && (
                <div style={{
                  width: "100%", maxWidth: "400px", height: "180px",
                  border: "1px solid #eee", borderRadius: "10px",
                  overflow: "hidden", marginBottom: "0.75rem", background: "#fafafa",
                }}>
                  <img
                    src={imagePreview || policy.ImageKey}
                    alt="Banner"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
              )}

              <input
                type="file"
                accept="image/*"
                id="cpImageInput"
                style={{ display: "none" }}
                onChange={handleImageSelect}
              />
              <button
                onClick={() => document.getElementById("cpImageInput").click()}
                style={{
                  background: "#2196f3", color: "#fff", border: "none",
                  padding: "0.5rem 1.2rem", borderRadius: "6px",
                  fontSize: "0.9rem", cursor: "pointer",
                }}
              >
                {policy.ImageKey ? "Change Image" : "Upload Image"}
              </button>

              {selectedImage && (
                <p style={{ fontSize: "0.82rem", color: "#4caf50", marginTop: "0.3rem" }}>
                  ✅ {selectedImage.name} — will upload on Save
                </p>
              )}
              {!selectedImage && policy.ImageKey && (
                <p style={{ fontSize: "0.82rem", color: "#888", marginTop: "0.3rem" }}>
                  ℹ️ Existing image will be kept
                </p>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", fontSize: "0.9rem", fontWeight: 500, color: "#333", marginBottom: "0.5rem" }}>
                Policy Content <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                rows={12}
                value={policy.description}
                onChange={(e) => setPolicy({ ...policy, description: e.target.value })}
                placeholder="Enter cancellation policy content..."
                style={{
                  width: "100%", padding: "0.75rem 1rem", border: "1px solid #ddd",
                  borderRadius: "8px", fontSize: "0.9rem", color: "#333",
                  resize: "vertical", outline: "none", boxSizing: "border-box",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                background: isSaving ? "#9e9e9e" : "#4caf50",
                color: "#fff", border: "none",
                padding: "0.6rem 1.5rem", borderRadius: "6px",
                fontSize: "0.95rem", cursor: isSaving ? "not-allowed" : "pointer",
                fontWeight: 500,
              }}
            >
              {isSaving ? "Saving..." : "Save Policy"}
            </button>
          </>
        ) : (
          /* View Mode */
          <div>
            {policy.title && (
              <h3 style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1a1a2e", marginBottom: "0.75rem" }}>
                {policy.title}
              </h3>
            )}

            {policy.ImageKey && (
              <div style={{
                width: "100%", maxWidth: "400px", height: "180px",
                border: "1px solid #eee", borderRadius: "10px",
                overflow: "hidden", marginBottom: "1rem",
              }}>
                <img
                  src={policy.ImageKey}
                  alt="Banner"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            )}

            <p style={{
              fontSize: "0.9rem", color: "#555", lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}>
              {policy.description || "No cancellation policy content added yet."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancellationPolicyAdmin;